"use client";

import { c as _c } from "react/compiler-runtime";
import { identifyUser, optInCapturing, optOutCapturing, resetUser, trackOrgSwitched } from "@/lib/analytics";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useUser, useProfile } from "@b3dotfun/sdk/global-account/react";
import { useAccountWallet } from "@b3dotfun/sdk/global-account/react";
import { useEffect, useRef } from "react";

/**
 * PostHog User Identification Component
 *
 * This component handles identifying users in PostHog with their traits
 * when they authenticate. It should be placed in the v2 layout.
 *
 * Identifies users with:
 * - b3GlobalId (distinct_id)
 * - username
 * - profile name
 * - wallet address
 * - current organization
 *
 * Uses useAppAuth + useUser instead of useAuth to avoid triggering
 * Thirdweb's useAutoConnect, which races with BetterAuthProvider and
 * causes the login form to flash on the OAuth callback page.
 */
export function PostHogIdentify() {
  const $ = _c(25);
  const {
    user
  } = useUser();
  const {
    isAuthenticated,
    isAuthenticating
  } = useAppAuth();
  const authReady = !isAuthenticating;
  const shouldFetchProfile = authReady && isAuthenticated && !!user?.userId;
  const t0 = shouldFetchProfile ? user?.userId : undefined;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      address: undefined,
      name: undefined,
      b3GlobalId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: profileData
  } = useProfile(t1);
  const {
    address: walletAddress
  } = useAccountWallet();
  const {
    selectedOrgId,
    organizations
  } = useOrganizations();
  let t2;
  if ($[2] !== organizations || $[3] !== selectedOrgId) {
    let t3;
    if ($[5] !== selectedOrgId) {
      t3 = org => org.id === selectedOrgId;
      $[5] = selectedOrgId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = organizations.find(t3);
    $[2] = organizations;
    $[3] = selectedOrgId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const selectedOrg = t2;
  const prevAuthRef = useRef(false);
  const prevOrgIdRef = useRef(null);
  const hasIdentifiedRef = useRef(false);
  let t3;
  if ($[7] !== isAuthenticated || $[8] !== profileData || $[9] !== selectedOrg || $[10] !== selectedOrgId || $[11] !== user || $[12] !== walletAddress) {
    t3 = () => {
      if (isAuthenticated && user?.userId) {
        optInCapturing();
        const traits = {
          b3_global_id: user.userId
        };
        if (user.username) {
          traits.username = user.username;
        }
        if (profileData?.name) {
          traits.name = profileData.name;
        }
        if (walletAddress) {
          traits.wallet_address = walletAddress;
        }
        if (selectedOrgId) {
          traits.org_id = selectedOrgId;
        }
        if (selectedOrg?.name) {
          traits.org_name = selectedOrg.name;
        }
        identifyUser(user.userId, traits);
        hasIdentifiedRef.current = true;
      }
      if (prevAuthRef.current && !isAuthenticated) {
        resetUser();
        optOutCapturing();
        hasIdentifiedRef.current = false;
      }
      prevAuthRef.current = isAuthenticated;
    };
    $[7] = isAuthenticated;
    $[8] = profileData;
    $[9] = selectedOrg;
    $[10] = selectedOrgId;
    $[11] = user;
    $[12] = walletAddress;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = user?.userId;
  const t5 = user?.username;
  const t6 = profileData?.name;
  const t7 = selectedOrg?.name;
  let t8;
  if ($[14] !== isAuthenticated || $[15] !== selectedOrgId || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== walletAddress) {
    t8 = [isAuthenticated, t4, t5, t6, walletAddress, selectedOrgId, t7];
    $[14] = isAuthenticated;
    $[15] = selectedOrgId;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = walletAddress;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t3, t8);
  let t10;
  let t9;
  if ($[22] !== selectedOrgId) {
    t9 = () => {
      if (hasIdentifiedRef.current && selectedOrgId && prevOrgIdRef.current !== null && prevOrgIdRef.current !== selectedOrgId) {
        trackOrgSwitched(selectedOrgId);
      }
      prevOrgIdRef.current = selectedOrgId;
    };
    t10 = [selectedOrgId];
    $[22] = selectedOrgId;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  return null;
}